/**
 * React hooks that resolve the active feature set and assembled config for the
 * current instance.
 *
 * `useEnabledFeatures` is the canonical answer to "which features are on right
 * now" — it merges (in order) structure module defaults, deployment-config silo
 * defaults, and the instance's own toggles. `useConfig` and `useScreenSet` read
 * the assembled config that `Datastore` puts on `ConfigContext`.
 *
 * Only a space and a story carry toggles of their own — a space's are a field
 * of the space resource, a story's a field of the stories resource — and that
 * split is resolved once, at the mount, into the `activeFeatures` `Datastore`
 * puts on `ConfigContext` alongside `config`/`screenSet`, which
 * `useInstanceFeatures` reads here. `useSetFeatures` still branches by
 * structure itself, since a write has no assembled value to read back.
 */

import { type Capability, FEATURES_TOGGLE_STABLE_CAPABILITY } from '@repo/shared/roles-capabilities';
import { useLanguage } from 'component/translation';
import { useSetSpaceFeatures } from 'data/spaces';
import { useSetStoryFeatures } from 'data/story-features';
import { useContext, useMemo } from 'react';
import { useHasCapability } from 'util/admin';
import { SPACE_STRUCTURE_KEY, STORY_STRUCTURE_KEY, structureHasFeatureToggles } from 'util/structure-keys';

import type { EnabledFeaturesMap } from './datastore';
import { ConfigContext, useInstanceKey, useSiloKey, useStructureKey, useStructureModule } from './datastore';
import { getDeploymentDefaultFeatures } from './features';
import { EMPTY_OBJECT } from './stdtypes';

export function useConfig<T>(): T {
    const contextValue = useContext(ConfigContext);
    return (contextValue?.config ?? EMPTY_OBJECT) as T;
}

export function useScreenSet(): Record<string, any> {
    const contextValue = useContext(ConfigContext);
    return contextValue?.screenSet ?? EMPTY_OBJECT;
}

/**
 * The current instance's own feature toggles — the defaults are *not* merged
 * in, so this is what an admin explicitly turned on or off, and it is empty on
 * every structure but a space and a story. Most callers want
 * `useEnabledFeatures` instead.
 */
export function useInstanceFeatures(): Record<string, boolean> {
    const contextValue = useContext(ConfigContext);
    return contextValue?.activeFeatures ?? EMPTY_OBJECT;
}

/**
 * Turn features on or off on the current instance, for the two structures that
 * have toggles of their own. Both endpoints merge the patch server-side, so a
 * pick-one group can flip the old option off and the new one on in one write
 * rather than two.
 *
 * Exists for the feature toggle menu, the one caller that works across
 * structures. Product UI that toggles one known feature should call that
 * resource's own hook instead.
 *
 * Throws on any other structure rather than writing somewhere: the menu gates
 * itself on `structureHasFeatureToggles` before it renders a switch, so
 * reaching here means a new caller skipped that gate.
 */
export function useSetFeatures(): { setFeaturesAsync: (features: Record<string, boolean>) => Promise<unknown> } {
    const siloKey = useSiloKey();
    const locale = useLanguage();
    const structureKey = useStructureKey();
    const instanceKey = useInstanceKey();
    const { setSpaceFeaturesAsync } = useSetSpaceFeatures();
    const { setStoryFeaturesAsync } = useSetStoryFeatures({ siloKey, storyKey: instanceKey, locale });

    if (structureKey === STORY_STRUCTURE_KEY) {
        return { setFeaturesAsync: setStoryFeaturesAsync };
    }
    if (structureKey === SPACE_STRUCTURE_KEY) {
        return { setFeaturesAsync: setSpaceFeaturesAsync };
    }
    return {
        setFeaturesAsync: () => Promise.reject(new Error(`The '${structureKey}' structure has no per-instance feature toggles to write`)),
    };
}

/**
 * The capability the endpoint behind `useSetFeatures` requires on a structure.
 */
function featureTogglesCapability(structureKey: string): Capability | undefined {
    if (structureKey === SPACE_STRUCTURE_KEY || structureKey === STORY_STRUCTURE_KEY) return FEATURES_TOGGLE_STABLE_CAPABILITY;
    return undefined;
}

/**
 * Whether the current instance's features are editable — the render gate the
 * feature toggle menu uses, so the menu never reads a structure module's
 * `features` array on a structure that cannot write one back.
 */
export function useHasFeatureToggles(): boolean {
    return structureHasFeatureToggles(useStructureKey());
}

/**
 * Whether the current user holds the capability `useSetFeatures` needs on the
 * current structure.
 */
export function useCanSetFeatures(): boolean {
    return useHasCapability(featureTogglesCapability(useStructureKey()));
}

export function useEnabledFeatures(): EnabledFeaturesMap {
    const turnedOnFeatures = useInstanceFeatures();
    const mergedDefaultFeatures = useMergedDefaultFeatures();

    return { ...mergedDefaultFeatures, ...turnedOnFeatures };
}

export function useMergedDefaultFeatures(): EnabledFeaturesMap {
    const structureModule = useStructureModule();
    const defaultFeatures = structureModule.defaultFeatures;
    const siloKey = useSiloKey();
    const structureKey = useStructureKey();
    const deploymentDefaults = getDeploymentDefaultFeatures(siloKey, structureKey);
    return useMemo(() => ({ ...defaultFeatures, ...deploymentDefaults }), [defaultFeatures, deploymentDefaults]);
}
